import { useEffect, useState } from "react";

/** Matches the --bp-desktop breakpoint used across the layout CSS. */
const DESKTOP_QUERY = "(min-width: 1024px)";

/**
 * Reports whether the viewport is at/above the desktop breakpoint.
 *
 * Used for the handful of places where layout differs *behaviorally*,
 * not just visually — e.g. the schedule opening a detail pane instead
 * of navigating. Pure visual responsiveness should live in CSS.
 *
 * Guards against environments without `matchMedia` (jsdom/SSR) by
 * defaulting to the mobile path, so component tests render the mobile
 * layout deterministically.
 */
export function useIsDesktop(): boolean {
  const [isDesktop, setIsDesktop] = useState<boolean>(() => {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    return window.matchMedia(DESKTOP_QUERY).matches;
  });

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mql = window.matchMedia(DESKTOP_QUERY);
    const onChange = (e: MediaQueryListEvent) => setIsDesktop(e.matches);
    setIsDesktop(mql.matches);
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, []);

  return isDesktop;
}
